/* eslint-disable react/no-array-index-key */
import { useEffect, useState } from "react";

import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";

import AddIcon from "@mui/icons-material/Add";
// import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ErrorIcon from "@mui/icons-material/Error";
// import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import DeleteIcon from "@mui/icons-material/Delete";
import CopyIcon from "@mui/icons-material/CopyAll";
import NextIcon from "@mui/icons-material/ArrowForward";

import FormField from "../../../components/FormField";
import ModeFilter from "../../../components/filters/ModeFilter";
import ProductFilter from "../../../components/filters/ProductFilter";
import CompanyFilter from "../../../components/filters/CompanyFilter";
import CircleIcon from "../../../components/CircleIcon";
import NumberTextfield from "../../../components/NumberTextfield";
import IIndicationsRequest from "../types/IIndicationsRequest";
import OriginPrice from "./OriginPrice";
import MonthPicker from "./monthPicker";
import { TeamsIcon } from "../../../icons/Teams";


import productMapping from "../lib/productMapping";
import { useIsMobile } from "../../../hooks/useIsMobile";
import CopyToClipboardButton from "../../../utils/copyToClipboardButton";

const getError = (errors: any, key: string) => {
  const record = errors[key];

  if (!record) return undefined;

  return record.message;
};

const IndicationDetails = ({
  index,
  total,
  nextIndication,
  deleteIndication,
  copyIndication,
  disabled,
  disablePostToTeams,
}: {
  index: number;
  total: number;
  nextIndication: () => void;
  deleteIndication: () => void;
  copyIndication: () => void;
  disabled?: boolean;
  disablePostToTeams?: boolean;
}) => {
  const isMobile = useIsMobile(768);
  const form = useFormContext<IIndicationsRequest>();
  const originPrices = useFieldArray({
    control: form.control,
    name: `indications.${index}.prices`,
    keyName: "key",
  });

  const [open, setOpen] = useState(true);

  // ################################################################################################################################
  // ## VALIDATION ##################################################################################################################
  // ################################################################################################################################

  const product = form.watch(`indications.${index}.product`);
  const mode = form.watch(`indications.${index}.mode`);
  const startDate = form.watch(`indications.${index}.startDate`);
  const endDate = form.watch(`indications.${index}.endDate`);
  const quantity = form.watch(`indications.${index}.quantity`);

  const [originPrice, setOriginPrice] = useState<{
    origin: string;
    price?: number;
    minPrice?: number;
    maxPrice?: number;
  }>({
    origin: "",
  });

  const [disableOriginPrices, setDisableOriginPrices] = useState(false);
  useEffect(() => {
    if (product && mode && startDate && endDate) {
      setDisableOriginPrices(true);
    } else {
      setDisableOriginPrices(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, mode, startDate, endDate, quantity]);

  const handleNextIndication = () => {
    form.getFieldState(`indications.${index}.mode`);
    nextIndication();
  };

  useEffect(() => {
    if (index < total - 1) {
      setOpen(false);
    } else if (index === total - 1) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const addIndicationOriginPrice = () => {
    const { priceType } = form.getValues().indications[index];
    if (priceType === "single") {
      originPrices.append({
        origin: originPrice.origin,
        fobPrice: originPrice.price || 0,
      });
    } else {
      originPrices.append({
        origin: originPrice.origin,
        fobMinPrice: originPrice.minPrice || 0,
        fobMaxPrice: originPrice.maxPrice || 0,
      });
    }

    setOriginPrice({ origin: "" });
  };

  const deleteIndicationOriginPrice = (priceIndex: number) => {
    originPrices.remove(priceIndex);
  };

  const [errorCount, setErrorCount] = useState(0);
  useEffect(() => {
    const { errors } = form.formState;

    const records = Object.keys(errors).filter((key) =>
      key.startsWith(`indications[${index}]`)
    );

    setErrorCount(records.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState]);

  const [allowPostToTeams, setAllowPostToTeams] = useState(false);
  useEffect(() => {
    if (product) {
      if (productMapping[product]) {
        setAllowPostToTeams(true);
      } else {
        setAllowPostToTeams(false);
        form.setValue(`indications.${index}.postToTeams`, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, product]);

  return (
    <fieldset
      key={index}
      disabled={disabled}
      style={{
        border: "3px solid #148dd3",
        borderRadius: 4,
        opacity: disabled ? "50%" : undefined,
        padding: "20px 20px",
        display: "flex",
        flexDirection: "column",
        gap: open ? 20 : 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: 20,
        }}
      >
        <div
          style={{
            color: "#148DD3",
            fontWeight: 700,
            fontSize: 12,
            textTransform: "uppercase",
          }}
        >
          Competitor information
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
          {!open && errorCount > 0 && (
            <div>
              <ErrorIcon color="error" />
              {/* {errorCount} */}
            </div>
          )}

          <IconButton
            style={{ padding: 4, margin: 0 }}
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <ExpandLess style={{ color: "#005CA6" }} />
            ) : (
              <ExpandMore style={{ color: "#005CA6" }} />
            )}
          </IconButton>
        </div>
      </div>

      {/* CLOSED */}
      <div
        style={{
          display: open ? "none" : "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <CircleIcon
          fontSize={12}
          color="white"
          backgroundColor="#0F2C52"
          size={20}
          label={String(index + 1)}
        />
        <div style={{ color: "#595959", fontSize: 12, fontWeight: 400 }}>
          {`${product} / ${mode}`}
        </div>
      </div>
      {/* END CLOSED */}

      {/* OPEN */}
      <div style={{ display: !open ? "none" : undefined }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 300,
              color: "#595959",
              width: "50%",
            }}
          >
            All fields required unless otherwise indicated.
          </div>
          <div style={{ width: 150 }}>
            <Controller
              control={form.control}
              name={`indications.${index}.priceType`}
              render={({ field }) => (
                <ToggleButtonGroup
                  disabled={form.formState.isSubmitting}
                  fullWidth
                  size="small"
                  value={field.value}
                  exclusive
                  onChange={(e, value) => {
                    originPrices.fields.forEach((r, i) => {
                      if (value === "single") {
                        form.setValue(
                          `indications.${index}.prices.${i}.fobPrice`,
                          0
                        );
                        form.setValue(
                          `indications.${index}.prices.${i}.fobMinPrice`,
                          undefined
                        );
                        form.setValue(
                          `indications.${index}.prices.${i}.fobMaxPrice`,
                          undefined
                        );
                      } else {
                        form.setValue(
                          `indications.${index}.prices.${i}.fobPrice`,
                          undefined
                        );
                        form.setValue(
                          `indications.${index}.prices.${i}.fobMinPrice`,
                          0
                        );
                        form.setValue(
                          `indications.${index}.prices.${i}.fobMaxPrice`,
                          0
                        );
                      }
                    });

                    field.onChange(value);
                  }}
                >
                  <ToggleButton value="single">Single</ToggleButton>
                  <ToggleButton value="range">Range</ToggleButton>
                </ToggleButtonGroup>
              )}
            />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{
              height: 64,
              display: "flex",
              alignItems: "flex-start",
              gap: 30,
            }}
          >
            <Controller
              name={`indications.${index}.product`}
              control={form.control}
              render={({ field }) => (
                <FormField
                  label="Product"
                  errorMessage={getError(
                    form.formState.errors,
                    `indications[${index}].product`
                  )}
                >
                  <ProductFilter
                    disabled={disabled}
                    value={field.value}
                    setValue={field.onChange}
                  />
                </FormField>
              )}
            />

            <Controller
              name={`indications.${index}.mode`}
              control={form.control}
              render={({ field }) => (
                <FormField
                  label="Mode"
                  errorMessage={getError(
                    form.formState.errors,
                    `indications[${index}].mode`
                  )}
                >
                  <ModeFilter
                    disabled={disabled}
                    value={field.value || ""}
                    setValue={field.onChange}
                    type="autocomplete"
                  />
                </FormField>
              )}
            />
          </div>
          <div
            style={{
              height: 64,
              maxWidth: "100%",
              display: "flex",
              alignItems: "flex-start",
              gap: 30,
            }}
          >
            <Controller
              control={form.control}
              name={`indications.${index}.competitor`}
              render={({ field }) => (
                <FormField
                  label={
                    <div
                      style={{ display: "flex", gap: 4, alignItems: "center" }}
                    >
                      Competitor
                      <div style={{ fontWeight: 400 }}>(if known)</div>
                    </div>
                  }
                  errorMessage={getError(
                    form.formState.errors,
                    `indications[${index}].competitor`
                  )}
                >
                  <CompanyFilter
                    disabled={disabled}
                    value={field.value}
                    setValue={field.onChange}
                    isMobile={isMobile}
                  />
                </FormField>
              )}
            />
            <Controller
              control={form.control}
              name={`indications.${index}.quantity`}
              render={({ field }) => (
                <FormField
                  label="Quantity"
                  errorMessage={getError(
                    form.formState.errors,
                    `indications[${index}].quantity`
                  )}
                >
                  <NumberTextfield
                    disabled={disabled}
                    size="small"
                    fullWidth
                    placeholder="Enter quantity"
                    value={field.value}
                    setValue={(value: number) => {
                      field.onChange(value);
                    }}
                  />
                </FormField>
              )}
            />
          </div>
          <div
            style={{
              height: 64,
            }}
          >
            <FormField
              label="Window"
              errorMessage={
                getError(
                  form.formState.errors,
                  `indications[${index}].startDate`
                ) ||
                getError(form.formState.errors, `indications[${index}].endDate`)
              }
            >
              <div style={{ display: "flex", gap: 30 }}>
                {/* <LocalizationProvider dateAdapter={AdapterDateFns}> */}
                <Controller
                  control={form.control}
                  name={`indications.${index}.startDate`}
                  render={({ field }) => (
                    <MonthPicker
                      disabled={disabled}
                      value={field.value as string}
                      setValue={(value: string) => {
                        field.onChange(value);
                      }}
                      type="start"
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name={`indications.${index}.endDate`}
                  render={({ field }) => (
                    <MonthPicker
                      disabled={disabled}
                      value={field.value as string}
                      setValue={(value: string) => {
                        field.onChange(value);
                      }}
                      type="end"
                    />
                  )}
                />
              </div>
            </FormField>
          </div>
          <div
            style={{
              paddingBottom: 14,
            }}
          >
            <FormField
              label={
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  Comments <div style={{ fontWeight: 400 }}>(optional)</div>
                </div>
              }
              errorMessage={getError(
                form.formState.errors,
                `indications[${index}].comments`
              )}
            >
              <TextField
                disabled={disabled}
                size="small"
                fullWidth
                multiline
                minRows={1}
                maxRows={3}
                placeholder="Enter comments"
                {...form.register(`indications.${index}.comments`)}
              />
            </FormField>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              paddingBottom: 8,
            }}
          >
            {originPrices.fields.map((r, i) => {
              return (
                <div
                  key={i}
                  style={{
                    backgroundColor: "#F2F8FD",
                    padding: "15px 20px",
                  }}
                >
                  <OriginPrice
                    indicationIndex={index}
                    originPriceIndex={i}
                    deleteIndication={() => deleteIndicationOriginPrice(i)}
                    disabled={disabled || !disableOriginPrices}
                  />
                </div>
              );
            })}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 15,
              }}
            >
              <div
                style={{
                  display: "flex",
                  color: "#C52F00",
                  fontSize: 12,
                }}
              >
                {originPrices.fields.length === 0 &&
                  getError(
                    form.formState.errors,
                    `indications[${index}].prices`
                  ) && (
                    <div>
                      {getError(
                        form.formState.errors,
                        `indications[${index}].prices`
                      )}
                    </div>
                  )}
              </div>
              <Button
                id="addIndicationOriginPriceButton"
                style={{
                  minWidth: 180,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  textTransform: "uppercase",
                }}
                onClick={() => {
                  addIndicationOriginPrice();
                }}
                disabled={
                  disabled ||
                  !disableOriginPrices ||
                  form.formState.isSubmitting
                }
              >
                <AddIcon style={{ fontSize: 18 }} />
                Add Origin Price
              </Button>
            </div>
          </div>
          <div
            style={{
              paddingBottom: 8,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {!disablePostToTeams && (
              <Controller
                control={form.control}
                name={`indications.${index}.postToTeams`}
                render={({ field }) => (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      fontSize: 12,
                      color: "rgb(75,75,75)",
                      fontWeight: 400,
                      opacity: !allowPostToTeams ? 0.5 : 1,
                    }}
                  >
                    <TeamsIcon />
                    Post to Teams
                    <Checkbox
                      checked={field.value}
                      value={field.value}
                      onChange={(e, checked) => {
                        field.onChange(checked);
                      }}
                      disabled={
                        disabled ||
                        !allowPostToTeams ||
                        form.formState.isSubmitting
                      }
                      style={{ paddingLeft: 0, paddingRight: 0 }}
                    />
                  </div>
                )}
              />
            )}
            {disablePostToTeams && (
              // Temporary button on mobile since authentication to microsoft doesn't work on iphones
              <CopyToClipboardButton 
                form={form}
                index={index}
                buttonText="Copy to clipboard"
                variant="contained"
              />
            )}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <Button
              id="deleteIndicationButton"
              disabled={
                (index === 0 && total === 1) || form.formState.isSubmitting
              }
              variant="outlined"
              style={{
                display: "flex",
                gap: 6,
                width: "26%",
                fontSize: ".8em",
              }}
              color="error"
              onClick={deleteIndication}
            >
              <DeleteIcon style={{ fontSize: "1.5em" }} />
              DELETE
            </Button>
            <Button
              id="copyIndicationButton"
              disabled={form.formState.isSubmitting}
              variant="outlined"
              style={{
                display: "flex",
                gap: 10,
                width: "37%",
                fontSize: ".8em",
              }}
              onClick={copyIndication}
            >
              <CopyIcon style={{ fontSize: "1.5em" }} />
              {isMobile ? "COPY" : "COPY INDICATION"}
            </Button>
            <Button
              id="nextIndicationButton"
              disabled={form.formState.isSubmitting}
              variant="contained"
              style={{
                display: "flex",
                gap: 10,
                width: "37%",
                fontSize: ".8em",
              }}
              onClick={handleNextIndication}
            >
              <NextIcon style={{ fontSize: "1.5em" }} />
              {isMobile ? "NEXT" : "NEXT INDICATION"}
            </Button>
          </div>
        </div>
      </div>
      {/* END OPEN */}
    </fieldset>
  );
};

export default IndicationDetails;
